// Retires the hardcoded "★ 4.9" on the guest-visible public profile
// (task brief fabrication #1). app.js:1351's renderPublicProfile() paints
//
//     { n: "★ 4.9", l: `${u.ratings || 0} ratings` }
//
// as the 7th tile of its `.stats-row.stats-7` — a fake average beside a real
// (always-zero, since mapListingRow never had a source for it) count. The
// ratings system (api-server/src/routes/reviews.ts, GET
// /api/reviews/summary/:userId) now exists, so this is the same
// MutationObserver-on-#app pattern linkFixups.ts uses (see that file's own
// header for why an observer and not a wrapped render()), applied here
// instead to swap in the real average once it's fetched.
//
// WHY A SEPARATE OBSERVER, NOT A THIRD FIXUP INSIDE linkFixups.ts: that file
// is merged and reviewed and the task brief marks it off-limits. It's also a
// different SHAPE of problem — every one of its fixups is a synchronous DOM
// rewrite; this one needs a network round trip per profile view, so it
// cannot share linkFixups' single synchronous applyLinkFixups() pass without
// making that pass async and slowing down every OTHER fixup on every render.
// Two independent observers on the same #app node is normal DOM API usage —
// each fires its own callback for the same mutations, and this module's own
// text-only edits do not touch anything linkFixups' selectors match.
//
// STRUCTURAL MATCHING (never index position, per the task brief, same as
// linkFixups.ts): the profile root is found via `[data-follow]` — a control
// that only renderPublicProfile emits (renderProfile, the OTHER function
// that shares the same "★ 4.9" literal at app.js:1167, uses `data-follow`
// nowhere; its own actions are `#ph-listitem`/`#edit-profile`/`data-goaccount`
// — see the task report for why that second copy doesn't need a fixup at
// all). Reading `data-follow`'s value hands us the exact handle Blake used
// to look up USERS[handle], the same way linkFixups reads `data-store`. The
// ratings tile itself is found by its label text ending in "ratings" — the
// one property distinguishing it from the row's other five `.stat`/
// `.stat-link` tiles (Trades, Sales, Active Items, Collection Value,
// Followers, Following), none of whose labels can end that way.
//
// EMPTY STATE (task brief, non-negotiable): a user with no reviews gets
// `ratingAverage: null` from the API — see reviewAggregate.ts's own "🔴 THE
// EMPTY-STATE RULE". Production has 2 completed deals in all of platform
// history, so this is the common case, not an edge case. There is no star
// rating to show, so no star is shown: NO_RATING_SCORE/NO_RATING_LABEL below replace both
// the "★ 4.9" slot and the count slot with an honest "no score yet" state —
// see the task report for the fuller reasoning (the designer's markup has no
// third "we don't have this" slot to render into, same gap floorTransport.ts's
// own comment/commentCount gap-log entry names for a different field, so the
// smallest honest treatment is a text swap, not a fabricated placeholder).

const NO_RATING_SCORE = "—";
const NO_RATING_LABEL = "No ratings yet";

/** What GET /api/reviews/summary/:userId returns (reviewAggregate.ts's
 *  ReviewSummary). Only the two fields this module reads are declared. */
interface ReviewSummary {
  ratingCount: number;
  ratingAverage: number | null;
}

/** Marks a ratings tile with the handle it was already fetched (or is
 *  in-flight) for, so a re-entrant observer callback — triggered by this
 *  module's own text edit below — finds nothing left to do for that handle.
 *  Per-handle, not a boolean, because navigating from one profile straight
 *  to another must refetch rather than staying stuck on the guard. */
const FIXED_FOR_ATTR = "data-gbz-rating-for";

interface RatingsTile {
  container: Element;
  handle: string;
  scoreEl: Element;
  labelEl: Element;
}

/** The ratings tile on EITHER profile view.
 *
 * 🔑 This used to anchor on `[data-follow]` alone, which only
 * renderPublicProfile emits. This file’s own header said the owner’s copy
 * "doesn’t need a fixup at all" — it does: renderProfile shares the same
 * hardcoded "★ 4.9" literal, so every signed-in user was looking at a
 * constant rating on their own profile, sitting next to an honest "0
 * ratings". The self view is found by `#edit-profile`, which renderProfile
 * emits and renderPublicProfile never does.
 *
 * The handle on the self view comes off `data-follist="<handle>:followers"`,
 * which renderProfile writes on its own stat cells. */
function findRatingsTile(doc: Document, selfHandleAllowed = true): RatingsTile | null {
  const followBtn = doc.querySelector(".profile.wrap [data-follow]");
  let handle = followBtn?.getAttribute("data-follow") ?? null;
  let root: Element | null = followBtn;
  if (!root || !handle) {
    if (!selfHandleAllowed) return null;
    const editBtn = doc.querySelector(".profile.wrap #edit-profile");
    if (!editBtn) return null; // on neither profile view this pass
    const selfWrap = editBtn.closest(".profile.wrap");
    const raw = selfWrap?.querySelector("[data-follist]")?.getAttribute("data-follist") ?? "";
    const selfHandle = raw.split(":")[0]?.trim();
    if (!selfWrap || !selfHandle) return null;
    root = editBtn;
    handle = selfHandle;
  }
  const wrap = root.closest(".profile.wrap");
  const statsRow = wrap?.querySelector(".stats-row.stats-7");
  if (!statsRow) return null;
  const container = Array.from(statsRow.querySelectorAll(".stat")).find((el) =>
    /ratings$/i.test(el.querySelector(".l.microlabel")?.textContent?.trim() ?? ""),
  );
  const scoreEl = container?.querySelector(".n");
  const labelEl = container?.querySelector(".l.microlabel");
  if (!container || !scoreEl || !labelEl) return null;
  return { container, handle, scoreEl, labelEl };
}

function paintNoRatings(tile: RatingsTile): void {
  tile.scoreEl.textContent = NO_RATING_SCORE;
  tile.labelEl.textContent = NO_RATING_LABEL;
}

function paintRatings(tile: RatingsTile, summary: ReviewSummary): void {
  if (summary.ratingAverage === null) {
    paintNoRatings(tile);
    return;
  }
  tile.scoreEl.textContent = `★ ${summary.ratingAverage.toFixed(1)}`;
  tile.labelEl.textContent = `${summary.ratingCount} rating${summary.ratingCount === 1 ? "" : "s"}`;
}

export function applyRatingFixup(
  doc: Document,
  authFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>,
  userIds: Record<string, string>,
): void {
  const tile = findRatingsTile(doc);
  if (!tile) return; // not on the public profile view this pass — legitimate absence
  if (tile.container.getAttribute(FIXED_FOR_ATTR) === tile.handle) return; // already fetched/fetching for this handle
  tile.container.setAttribute(FIXED_FOR_ATTR, tile.handle);

  const userId = userIds[tile.handle];
  if (!userId) {
    // This map has TWO producers, and both set a real users.id:
    // installFloorListings builds it from the floor (mapListingRow always sets
    // user.id from the wire's seller.id), and installLiveListingComments adds
    // each comment author as it arrives — those handles reach USERS too, so
    // without that second producer this branch would fire for every commenter
    // whose profile a visitor opened, turning a drift alarm into routine noise.
    // With both wired, a miss here still means the two have genuinely drifted
    // — loud, not a silent fabricated-looking zero.
    //
    // 🔴 AND THE FALLBACK IS NOT NEUTRAL. paintNoRatings writes "No ratings
    // yet", which is a CLAIM — false for anyone who has ratings we simply did
    // not fetch. That is why the id is worth plumbing rather than letting this
    // branch absorb a whole new population.
    console.warn(`[web-install] rating fixup: no known user id for handle "${tile.handle}"`);
    paintNoRatings(tile);
    return;
  }

  authFetch(`/api/reviews/summary/${userId}`)
    .then((res) => {
      if (!res.ok) throw new Error(`GET /api/reviews/summary/${userId} failed: ${res.status} ${res.statusText}`);
      return res.json() as Promise<ReviewSummary>;
    })
    .then((summary) => {
      // The visitor may have navigated to a different profile (or away
      // entirely — Blake's own render() destroys and rebuilds #app wholesale
      // on every route change) while this was in flight. Re-check both that
      // this is still the tile it was fetched for AND that it's still in the
      // live document before writing into it.
      if (!tile.container.isConnected || tile.container.getAttribute(FIXED_FOR_ATTR) !== tile.handle) return;
      paintRatings(tile, summary);
    })
    .catch((err) => {
      console.warn(`[web-install] rating fixup: failed to load ratings for "${tile.handle}"`, err);
      if (tile.container.isConnected && tile.container.getAttribute(FIXED_FOR_ATTR) === tile.handle) {
        paintNoRatings(tile);
      }
    });
}

/** Must run after app.js has loaded, same ordering requirement as
 *  installLinkFixups — see WebInstall.tsx's call site. `userIds` is the map
 *  installFloorListings already returns (handle -> users.id); this module
 *  fetches nothing that call didn't already know about the shape of. */
export function installRatingFixups(
  win: Window,
  authFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>,
  userIds: Record<string, string>,
): void {
  const doc = win.document;
  const app = doc.getElementById("app");
  if (!app) {
    console.warn("[web-install] installRatingFixups: #app not found, rating fixups not installed");
    return;
  }
  applyRatingFixup(doc, authFetch, userIds);
  const observer = new MutationObserver(() => {
    applyRatingFixup(doc, authFetch, userIds);
  });
  observer.observe(app, { childList: true, subtree: true });
}

// NO_RATING_SCORE/NO_RATING_LABEL exported for tests only, same as
// linkFixups.ts exporting applyLinkFixups alongside installLinkFixups.
export { NO_RATING_SCORE, NO_RATING_LABEL };
export type { ReviewSummary };
